import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useMutation } from '@apollo/client'
import { useSnackbar } from 'notistack'
import Dialog from 'components/dialog'
import { Box, TextField, Typography } from '@material-ui/core'
import DialogTitle from '@material-ui/core/DialogTitle'
import DialogContent from '@material-ui/core/DialogContent'
import DialogActions from '@material-ui/core/DialogActions'
import Button from '@material-ui/core/Button'
import { MuiPickersUtilsProvider } from '@material-ui/pickers'
import { useForm } from 'react-hook-form'
import CustomLuxonUtils from 'lib/CustomLuxonUtils'
import { useStyles } from './styles'
import TileButtons from './tile-buttons'
import { CREATE_STUDENT } from './mutation'
import { yupResolver } from '@hookform/resolvers'
import * as yup from 'yup'
import { useIsMobile } from 'lib/hooks'

const validationSchema = yup.object().shape({
  studentFirstName: yup
    .string()
    .min(1)
    .required(),
  studentLastName: yup
    .string()
    .min(1)
    .required(),
  email: yup
    .string()
    .email()
    .required(),
  phoneNumber: yup.string(),
  guardianFirstName: yup.string().min(1),
  guardianLastName: yup.string().min(1),
})

const formatPhoneNumber = num => {
  if (num) {
    const cleanedNum = num.replace(/\D/g, '')
    return `${cleanedNum}`
  }
  return null
}

const fieldProps = classes => ({
  variant: 'outlined',
  fullWidth: true,
  margin: 'none',
  className: classes.formField,
})

const CoachAddStudentModal = ({
  open,
  setOpen,
  onAdd,
  dialogTitle = 'Create Student',
  btnTitle = 'Create Student',
  initialValues = {},
  leadId,
}) => {
  const [createStudent] = useMutation(CREATE_STUDENT)
  const [isAdult, setIsAdult] = useState(true)
  const { register, handleSubmit, errors } = useForm({
    defaultValues: {
      studentFirstName: initialValues?.studentFirstName || '',
      studentLastName: initialValues?.studentLastName || '',
      email: initialValues?.email || '',
      phoneNumber: initialValues?.phoneNumber || '',
    },
    resolver: yupResolver(validationSchema),
  })

  const { enqueueSnackbar } = useSnackbar()
  const isMobile = useIsMobile()

  const classes = useStyles({ isAdult })
  const sharedFieldProps = fieldProps(classes)

  const onSubmit = async formValues => {
    formValues.isMinor = !isAdult
    formValues.phoneNumber = formatPhoneNumber(formValues.phoneNumber)
    formValues.coachingInquiryId = leadId
    try {
      const response = await createStudent({
        variables: { params: formValues },
      })
      enqueueSnackbar('Student Created', {
        variant: 'success',
      })
      setOpen(false)
      onAdd && onAdd(response?.data)
    } catch (e) {
      enqueueSnackbar('Sorry, an error occurred', {
        variant: 'error',
      })
      setOpen(false)
    }
  }

  return (
    <Dialog
      isOpen={open}
      onClose={() => setOpen(false)}
      showCloseButton
      fullWidth
      maxWidth="sm"
      fullScreen={isMobile}
      classes={{ paper: classes.dialogPaper }}
    >
      {() => (
        <>
          <DialogTitle classes={{ root: classes.titleRoot }}>
            {dialogTitle}
          </DialogTitle>
          <form onSubmit={handleSubmit(onSubmit)}>
            <MuiPickersUtilsProvider utils={CustomLuxonUtils}>
              <DialogContent classes={{ root: classes.dialogContent }}>
                <TileButtons {...{ isAdult, setIsAdult }} />
                <Box className={classes.fieldStack}>
                  {!isAdult ? (
                    <Typography className={classes.sectionLabel}>
                      Student Information
                    </Typography>
                  ) : null}
                  <Box className={classes.fieldRow}>
                    <TextField
                      name="studentFirstName"
                      inputRef={register}
                      label="First name *"
                      error={!!errors.studentFirstName}
                      helperText={
                        errors.studentFirstName && 'This field is required'
                      }
                      {...sharedFieldProps}
                    />
                    <TextField
                      name="studentLastName"
                      inputRef={register}
                      label="Last name *"
                      error={!!errors.studentLastName}
                      helperText={
                        errors.studentLastName && 'This field is required'
                      }
                      {...sharedFieldProps}
                    />
                  </Box>
                  {!isAdult ? (
                    <>
                      <Typography className={classes.sectionLabel}>
                        Guardian Information
                      </Typography>
                      <Box className={classes.fieldRow}>
                        <TextField
                          name="guardianFirstName"
                          inputRef={register}
                          label="First name *"
                          error={!!errors.guardianFirstName}
                          helperText={
                            errors.guardianFirstName && 'This field is required'
                          }
                          {...sharedFieldProps}
                        />
                        <TextField
                          name="guardianLastName"
                          inputRef={register}
                          label="Last name *"
                          error={!!errors.guardianLastName}
                          helperText={
                            errors.guardianLastName && 'This field is required'
                          }
                          {...sharedFieldProps}
                        />
                      </Box>
                    </>
                  ) : null}
                  <TextField
                    name="email"
                    inputRef={register}
                    label="Email *"
                    error={!!errors.email}
                    helperText={errors.email && 'This field is required'}
                    {...sharedFieldProps}
                  />
                  <TextField
                    name="phoneNumber"
                    inputRef={register}
                    onBlur={e => {
                      e.target.value = formatPhoneNumber(e.target.value)
                    }}
                    label="Phone"
                    error={!!errors.phoneNumber}
                    helperText={errors.phoneNumber && 'This field is required'}
                    {...sharedFieldProps}
                  />
                </Box>
              </DialogContent>
              <DialogActions className={classes.dialogActions}>
                <Button
                  color="primary"
                  size="large"
                  onClick={() => setOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="large"
                  disableElevation
                >
                  {btnTitle}
                </Button>
              </DialogActions>
            </MuiPickersUtilsProvider>
          </form>
        </>
      )}
    </Dialog>
  )
}

CoachAddStudentModal.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  onAdd: PropTypes.func,
  dialogTitle: PropTypes.string,
  btnTitle: PropTypes.string,
  initialValues: PropTypes.shape({
    studentFirstName: PropTypes.string,
    studentLastName: PropTypes.string,
    email: PropTypes.string,
    phoneNumber: PropTypes.string,
  }),
  leadId: PropTypes.string,
}

export default CoachAddStudentModal
